import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { PageHero } from "@/components/PageHero";
import { HERO_ART } from "@/lib/heroes";
import Seo from "@/components/Seo";
import { PenLine, Languages, Code2, Landmark, HandHeart, GraduationCap, ArrowUpRight, Mail, ShieldCheck } from "lucide-react";

const WAYS = [
  { icon: PenLine, en: "Contribute Content", hi: "सामग्री योगदान",
    d_en: "Share kathas, stotra meanings, temple details or scholarly notes. We credit every contributor.",
    d_hi: "कथाएँ, स्तोत्र अर्थ, मंदिर विवरण या विद्वत्तापूर्ण टिप्पणियाँ साझा करें। हम प्रत्येक योगदानकर्ता को श्रेय देते हैं।" },
  { icon: Languages, en: "Translate & Review", hi: "अनुवाद एवं समीक्षा",
    d_en: "Help translate the platform into more Bharatiya languages, or review existing Hindi/Tamil/Telugu text.",
    d_hi: "मंच का अधिक भारतीय भाषाओं में अनुवाद करने में सहायता करें, या मौजूदा पाठ की समीक्षा करें।" },
  { icon: GraduationCap, en: "Scholarly Review", hi: "शास्त्रीय समीक्षा",
    d_en: "Acharyas and students of the shastras can verify sources, citations and transliterations.",
    d_hi: "आचार्य एवं शास्त्र के विद्यार्थी स्रोतों, उद्धरणों एवं लिप्यंतरण का सत्यापन कर सकते हैं।" },
  { icon: Code2, en: "Build With Us", hi: "तकनीकी सहयोग",
    d_en: "Designers and developers — help us build the web and upcoming mobile apps.",
    d_hi: "डिज़ाइनर एवं डेवलपर — हमें वेब एवं आगामी मोबाइल ऐप बनाने में सहायता करें।" },
  { icon: Landmark, en: "Temple & Institution Partners", hi: "मंदिर एवं संस्था साझेदार",
    d_en: "Temples, mathas and trusts can partner for verified details, live darshan and seva listings.",
    d_hi: "मंदिर, मठ एवं न्यास सत्यापित विवरण, लाइव दर्शन एवं सेवा सूची हेतु साझेदारी कर सकते हैं।" },
  { icon: HandHeart, en: "Support & Donate", hi: "सहयोग एवं दान",
    d_en: "Fund hosting, content creation and the mission to keep Dharma knowledge free and accessible.",
    d_hi: "होस्टिंग, सामग्री निर्माण एवं धर्म-ज्ञान को निःशुल्क व सुलभ रखने के ध्येय हेतु सहयोग दें।" },
];

export default function Collaborate() {
  const { pick } = useLang();
  const { heading, copy, isVisible } = useSettings();
  const h = heading("collaborate.hero", { title_deva: "सहयोग करें", title_en: "Collaborate & Work With Us", overline: "Join the mission", sub_en: "Dharmartha is a community effort to preserve and share the knowledge traditions of Bharat. There are many ways to help — choose yours." });
  const intro = copy("collaborate.intro", "Whether you are a scholar, a devotee, a translator, a developer or a temple — your contribution helps keep this knowledge authentic, living and freely available.", "आप विद्वान हों, भक्त हों, अनुवादक, डेवलपर या मंदिर — आपका योगदान इस ज्ञान को प्रामाणिक, जीवंत एवं निःशुल्क बनाए रखने में सहायक है।");

  return (
    <div data-testid="collaborate-page">
      <Seo path="/collaborate" title="Collaborate & Work With Us — Dharmartha" description="Contribute content, translate, review, build or partner with Dharmartha." />
      <PageHero
        art={HERO_ART.devotion}
        overline={pick(h.overline || "सहभागिता", h.overline || "Join the mission")}
        deva={h.title_deva || "सहयोग करें"}
        en={pick(h.title_deva || "सहयोग करें", h.title_en || "Collaborate & Work With Us")}
        tagline={pick(h.sub_deva || h.sub_en, h.sub_en)}
      />

      <section className="mx-auto max-w-4xl px-5 py-12 md:px-8 md:py-16">
        <p className="mx-auto max-w-3xl text-center text-base leading-relaxed text-ink-2">{pick(intro.hi, intro.en)}</p>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {WAYS.map((w, i) => (
            <motion.div key={w.en} data-testid={`collaborate-way-${i}`}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}
              className="flex h-full flex-col border border-line bg-surface p-6">
              <span className="flex h-11 w-11 items-center justify-center border border-[var(--gold-muted)] text-gold"><w.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-serif-latin text-xl text-ink">{pick(w.hi, w.en)}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{pick(w.d_hi, w.d_en)}</p>
            </motion.div>
          ))}
        </div>

        {isVisible("/join-as-purohit") && (
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="mt-10 flex flex-col items-start gap-4 border border-[var(--saffron)]/40 bg-[var(--saffron)]/[0.06] p-7 sm:flex-row sm:items-center sm:justify-between"
            data-testid="collaborate-join-purohit">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--saffron)]/15 text-saffron"><ShieldCheck className="h-6 w-6" /></span>
              <div>
                <h3 className="font-serif-latin text-xl text-ink">{pick("पुरोहित, ज्योतिषी या आचार्य के रूप में सेवा दें", "Serve as a Purohit, Astrologer or Guru")}</h3>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-ink-2">{pick("सत्यापित सेवा प्रदाता बनें — पूजा, यज्ञ एवं ज्योतिष परामर्श हेतु साधकों से जुड़ें।", "Become a verified service provider — offer poojas, yagyas and jyotish consultations to devotees worldwide.")}</p>
              </div>
            </div>
            <Link to="/join-as-purohit" data-testid="collaborate-join-purohit-cta" className="inline-flex shrink-0 items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
              {pick("अभी आवेदन करें", "Apply now")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}

        <div className="mt-14 flex flex-col items-center gap-4 border border-line bg-[var(--parchment)] p-8 text-center" data-testid="collaborate-cta">
          <h2 className="font-serif-latin text-2xl text-ink">{pick("आइए मिलकर कार्य करें", "Let's work together")}</h2>
          <p className="max-w-xl text-sm text-ink-2">{pick("हमें अपनी रुचि बताएँ — हम आपसे संपर्क करेंगे।", "Tell us how you'd like to help — we'll get in touch.")}</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            <Link to="/feedback?type=suggestion&area=Collaborate" data-testid="collaborate-feedback" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]">
              {pick("सहयोग हेतु लिखें", "Share how you'd like to help")} <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/feedback?type=general" className="inline-flex items-center gap-2 border border-line px-5 py-2.5 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">
              <Mail className="h-4 w-4" /> {pick("संपर्क करें", "Contact Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
